import { useMemo } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { AbsenceDay } from '../../types/absence-day'
import Card from '../../shared/layout/Card'
import { StyledManAuthGrid } from '../MyActionsGrid/StyledMyActionsGrid'
import { baseGridSx } from '../AbsenceDrawerComponent/AbsenceLedgerModal/gridStyles'
import { useIsSplitPotsForUk } from '../../utils/Hooks/useIsSplitPotsForUk'
import { createManAuthAbsenceDetailColumns } from './AbsenceDetailColumns'
import { getManAuthColumns } from './ManAuthColumns'

export interface LegacyManAuthRow {
  id: string | number
  Date: string
  absentHours: number
  OSP: number | null
  payApproval: number | null
  Unpaid: number
}

interface AbsenceDetailCardProps {
  absenceDays: AbsenceDay[]
  rows: LegacyManAuthRow[]
  isSickness: boolean
  locationGroupName?: string
}

const cardSx = {
  borderRadius: '16px',
  border: '1px solid rgba(0,0,0,0.08)',
  backgroundColor: '#FFFFFF',
  overflow: 'hidden',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}

const titleRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}

const titleSx = {
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 500,
  fontSize: '17px',
  lineHeight: '17px',
  letterSpacing: 0,
  color: '#000000',
}

const dividerSx = { borderBottom: '1px solid rgba(0,0,0,0.08)' }

const gridSx = {
  ...baseGridSx,
  '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': { outline: 'transparent' },
  '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
}

export default function AbsenceDetailCard({ absenceDays, rows, isSickness, locationGroupName }: AbsenceDetailCardProps) {
  const isSplitPotsForUk = useIsSplitPotsForUk(locationGroupName)

  const splitPotsColumns = useMemo(() => createManAuthAbsenceDetailColumns(), [])
  const splitPotsRows = useMemo(() => absenceDays.map(day => ({ ...day })), [absenceDays])
  const legacyColumns = useMemo(() => getManAuthColumns(isSickness), [isSickness])

  if (!isSplitPotsForUk) {
    return (
      <Card title="Absence Detail" noDivider>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <StyledManAuthGrid
              rows={rows}
              columns={legacyColumns}
              disableSelectionOnClick
              autoHeight
              pagination={false}
              hideFooter
              getRowId={row => row.id}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'date', sort: 'asc' }],
                },
              }}
              componentsProps={{
                panel: {
                  sx: {
                    '& .MuiNativeSelect-select option': {
                      font: '13px "Poppins", sans-serif',
                    },
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      </Card>
    )
  }

  return (
    <Box sx={cardSx} data-testid="manauth-absence-detail-card">
      <Box sx={titleRowSx}>
        <Typography sx={titleSx}>Absence Detail</Typography>
      </Box>
      <Box sx={dividerSx} />
      <DataGridPro
        rows={splitPotsRows}
        columns={splitPotsColumns}
        disableSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        autoHeight
        pagination={false}
        hideFooter
        getRowId={row => row.id}
        rowHeight={40}
        headerHeight={48}
        initialState={{
          sorting: { sortModel: [{ field: 'date', sort: 'asc' }] },
        }}
        sx={gridSx}
      />
    </Box>
  )
}
